import { AsyncPipe, DecimalPipe } from '@angular/common';
import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  input,
  output,
  inject,
  AfterViewInit,
} from '@angular/core';
import {
  ReactiveFormsModule,
  FormsModule,
  UntypedFormGroup,
  UntypedFormBuilder,
  UntypedFormControl,
  Validators,
} from '@angular/forms';
import {
  AuthSevices,
  ClientService,
  PaymentService,
  ToastMessageService,
} from '@fitness-force/api';
import { AppErrors } from '@fitness-force/models';
import { ErrorMsgComponent } from '@fitness-force/errors';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DividerModule } from 'primeng/divider';
import { SelectModule } from 'primeng/select';
import { RippleModule } from 'primeng/ripple';
import { StyleClassModule } from 'primeng/styleclass';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TimelineModule } from 'primeng/timeline';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { PopoverModule } from 'primeng/popover';
import type {
  Invoice,
  NotificationError,
  Payments,
  RefundData,
  TENANTSINTERFACE,
  TENANT_MODEL,
  voidInvoiceRequest,
} from '@fitness-force/models';
import { RefundType, PdfType } from '@fitness-force/models';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PaymentStatus } from '@fitness-force/enum';
import {
  DataGridComponent,
  EmptyStateContainerComponent,
} from '@fitness-force/grid';
import { ActionsComponent } from '@fitness-force/grid-utility/action';
import { CurrencyComponent } from '@fitness-force/grid-utility/currency';
import { DateFormatedComponent } from '@fitness-force/grid-utility';
import { InvoiceStatusComponent } from '../../data-grid/utility/invoice-status/invoice-status.component';
import { Store } from '@ngrx/store';
import { TENANTS_INFO } from '@fitness-force/state';
import { CreditBalanceSidebarComponent } from '@fitness-force/billing';
import { FeatureFlagService } from '@fitness-force/shared-utils';
import { PaymentModeOtherComponent } from '../../data-grid/utility/payment-mode-other/payment-mode-other.component';
import { NumberPrefixComponent } from '@fitness-force/common-number-prefix';
import { SkeletonModule } from 'primeng/skeleton';
import { ProcessRefundComponent } from '@fitness-force/refund';

@Component({
  selector: 'payment-tab',
  templateUrl: './payment-tab.component.html',
  styleUrls: ['./payment-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    DividerModule,
    TimelineModule,
    TableModule,
    ReactiveFormsModule,
    FormsModule,
    ButtonModule,
    RippleModule,
    SelectModule,
    AvatarModule,
    TagModule,
    StyleClassModule,
    DialogModule,
    PdfViewerModule,
    PopoverModule,
    DataGridComponent,
    ErrorMsgComponent,
    CreditBalanceSidebarComponent,
    ProcessRefundComponent,
    SkeletonModule,
    TranslateModule,
    AsyncPipe,
    DecimalPipe,
  ],
})
export class PaymentTabComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly paymentService = inject(PaymentService);
  private readonly translate = inject(TranslateService);
  private readonly toastMessageService = inject(ToastMessageService);
  private readonly fb = inject(UntypedFormBuilder);
  private readonly clientService = inject(ClientService);
  private readonly store = inject(Store);
  private readonly authService = inject(AuthSevices);
  private readonly cd = inject(ChangeDetectorRef);
  private readonly featureFlagService = inject(FeatureFlagService);

  readonly clientId = input<number>(0);
  readonly tenantId = input<number>(0);
  readonly showTableActions = input<boolean>(true);
  private readonly destroy$ = new Subject<void>();
  readonly showHeader = input<boolean>(true);

  readonly tabAction = output<{
    closePopup: boolean;
    reloadData: boolean;
  }>();
  readonly updateData = output<{ action: string }>();

  paymentData$ = new BehaviorSubject<Payments[]>([]);
  paymentTablecolumns$ = new BehaviorSubject<any>([]);
  paymentDetails: Payments;
  invoiceDetails: Invoice;
  paymentId: number = 0;
  invoiceId: number = 0;
  showPaymentPopup = new BehaviorSubject(false);
  showVoidPaymentModal: boolean = false;
  voidPaymentForm: UntypedFormGroup = new UntypedFormGroup({});
  pdfSrc = new BehaviorSubject<string>('');

  showBulkActionBar = false;
  selecallInvoiceData: boolean;
  showInvoicePopup: boolean = false;
  emptyStateComponent$ = new BehaviorSubject<any>(EmptyStateContainerComponent);
  getAccessTenantList$: Observable<any> | any | null;
  accessedTenants: TENANT_MODEL[] = [];
  allTenants: TENANT_MODEL[] = [];
  showAddCreditButton: boolean;
  addCreditSidebar: boolean = false;
  btnLoading = new BehaviorSubject<boolean>(false);
  number_prefix: string;

  showRefund = new BehaviorSubject<boolean>(false);
  refundData: RefundData = {} as RefundData;

  paymentParams: any = new Map();
  totalRecordLength = new BehaviorSubject({
    totalPage: 10,
    totalItems: 10,
    currentPage: 0,
  });
  totalItems$ = new BehaviorSubject(0);

  loading: boolean = false;

  ngOnInit(): void {
    this.paymentParams.set('PageNumber', { name: 'PageNumber', value: 1 });
    this.paymentParams.set('PageSize', { name: 'PageSize', value: 100 });
    this.GetPayments();
    this.getAccessTenantList$ = this.store.select(TENANTS_INFO);
    this.getAccessTenantList$.subscribe((tenant: TENANTSINTERFACE) => {
      this.accessedTenants = tenant.accessed_tenant;
      this.allTenants = tenant.all_tenant;
    });

    // Use getFlag$() observable to wait for Statsig initialization
    this.featureFlagService
      .getFlag$('customer_credit_balance')
      .pipe(takeUntil(this.destroy$))
      .subscribe((isEnabled) => {
        this.showAddCreditButton = isEnabled;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit() {
    this.voidPaymentForm = this.fb.group({
      void_reason: ['', [Validators.required]],
    });
  }

  GetPayments() {
    this.loading = true;
    this.clientService
      .getClientPayments(this.clientId(), this.getPaymentParams)
      .subscribe({
        next: (data: any) => {
          this.totalRecordLength.next({
            totalPage: data.total_pages,
            totalItems: data.total_items,
            currentPage: data.current_page,
          });
          this.totalItems$.next(data.total_items);
          this.setClientPayments(data.items ? data.items : []);
          this.loading = false;
        },
        error: () => {
          this.setClientPayments([]);
          this.loading = false;
        },
      });
  }

  setClientPayments(payments: Payments[]) {
    payments.map((p) => {
      p.invoiceNumbers = [];
      p.client_id = this.clientId();
      p.invoices.forEach((inv) => p.invoiceNumbers.push(inv.number_prefix));
      if (p.collected_at_tenant_id > 0) {
        if (
          this.accessedTenants.find((t) => t.id == p.collected_at_tenant_id)
        ) {
          p.collected_at_tenant_name =
            this.accessedTenants.find((t) => t.id == p.collected_at_tenant_id)!
              .name_primary_language ||
            this.accessedTenants.find((t) => t.id == p.collected_at_tenant_id)!
              .name_secondary_language;
        } else if (
          this.allTenants.find((t) => t.id == p.collected_at_tenant_id)
        ) {
          p.collected_at_tenant_name =
            this.allTenants.find((t) => t.id == p.collected_at_tenant_id)!
              .name_primary_language ||
            this.accessedTenants.find((t) => t.id == p.collected_at_tenant_id)!
              .name_secondary_language;
        } else p.collected_at_tenant_name = '-';
      }
      if (p.collected_for_tenant_id > 0) {
        if (
          this.accessedTenants.find((t) => t.id == p.collected_for_tenant_id)
        ) {
          p.collected_for_tenant_name =
            this.accessedTenants.find((t) => t.id == p.collected_for_tenant_id)!
              .name_primary_language ||
            this.accessedTenants.find((t) => t.id == p.collected_for_tenant_id)!
              .name_secondary_language;
        } else if (
          this.allTenants.find((t) => t.id == p.collected_for_tenant_id)
        ) {
          p.collected_for_tenant_name =
            this.allTenants.find((t) => t.id == p.collected_for_tenant_id)!
              .name_primary_language ||
            this.accessedTenants.find((t) => t.id == p.collected_for_tenant_id)!
              .name_secondary_language;
        } else p.collected_for_tenant_name = '-';
      }
    });
    this.paymentData$.next(payments);
    this.selecallInvoiceData = false;
    this.GetTranslatedColums();
  }

  GetTranslatedColums() {
    this.translate
      .get([
        'PAYMENT.COLUMNS.DATE_TIME',
        'PAYMENT.COLUMNS.PAYMENT_ID',
        'PAYMENT.COLUMNS.INVOICE',
        'PAYMENT.COLUMNS.PAYMENT_MODE',
        'PAYMENT.COLUMNS.AMOUNT_RECEIVED',
        'PAYMENT.COLUMNS.BILLED_AT',
        'PAYMENT.COLUMNS.BILLED_FOR',
        'PAYMENT.COLUMNS.STATUS',
      ])
      .subscribe((translations) => {
        this.GetColumns(translations);
      });
  }

  GetColumns(translations: any) {
    const columns = {
      DateTime: translations['PAYMENT.COLUMNS.DATE_TIME'],
      PaymentId: translations['PAYMENT.COLUMNS.PAYMENT_ID'],
      Invoice: translations['PAYMENT.COLUMNS.INVOICE'],
      PaymentMode: translations['PAYMENT.COLUMNS.PAYMENT_MODE'],
      AmountReceived: translations['PAYMENT.COLUMNS.AMOUNT_RECEIVED'],
      BillAt: translations['PAYMENT.COLUMNS.BILLED_AT'],
      BillFor: translations['PAYMENT.COLUMNS.BILLED_FOR'],
      Status: translations['PAYMENT.COLUMNS.STATUS'],
    };

    this.paymentTablecolumns$.next([
      {
        title: columns.DateTime,
        classes: ' py-2 text-600 w-15rem white-space-nowrap',
        sortable: true,
        filterable: true,
        selectable: false,
        dataProperty: 'payment_date',
        type: 'component',
        data: {
          name: DateFormatedComponent,
          additionData: true,
          aData: { KeyName: 'payment_date', format: 'd MMM yy' },
        },
      },
      {
        title: columns.PaymentId,
        classes: ' py-2 text-600 w-15rem white-space-nowrap',
        sortable: true,
        filterable: true,
        selectable: false,
        dataProperty: 'number_prefix',
        type: 'component',
        data: {
          name: NumberPrefixComponent,
          additionData: true,
          aData: {
            fetch_key: 'number_prefix',
            type: PdfType.Payment,
            id: 'id',
          },
        },
      },
      {
        title: columns.Invoice,
        classes: ' py-2 text-600 w-15rem white-space-nowrap',
        sortable: true,
        filterable: true,
        selectable: false,
        dataProperty: 'invoiceNumbers',
        type: 'component',
        data: {
          name: NumberPrefixComponent,
          additionData: true,
          aData: {
            fetch_key: 'invoiceNumbers',
            type: PdfType.Invoice,
            id: 'id',
          },
        },
      },
      {
        title: columns.PaymentMode,
        classes: ' py-2 text-600 w-15rem white-space-nowrap',
        sortable: true,
        filterable: true,
        selectable: false,
        type: 'component',
        dataProperty: 'payment_mode_level2_id',
        data: {
          name: PaymentModeOtherComponent,
          additionData: true,
          aData: { fetch_key: 'payment_mode_level2_id' },
        },
      },
      {
        title: columns.AmountReceived,
        classes: ' py-2 text-600 w-15rem white-space-nowrap',
        sortable: true,
        filterable: true,
        selectable: false,
        dataProperty: 'amount_in_minor_currency',
        type: 'component',
        data: {
          name: CurrencyComponent,
          fetch_key: 'amount_in_minor_currency',
        },
      },
      {
        title: columns.BillAt,
        classes: ' py-2 text-600 w-15rem white-space-nowrap',
        sortable: true,
        filterable: true,
        selectable: false,
        dataProperty: 'collected_at_tenant_name',
        type: 'default',
      },
      {
        title: columns.BillFor,
        classes: ' py-2 text-600 w-15rem white-space-nowrap',
        sortable: true,
        filterable: true,
        selectable: false,
        dataProperty: 'collected_for_tenant_name',
        type: 'default',
      },
      {
        title: columns.Status,
        classes: ' py-2 text-600 w-15rem white-space-nowrap',
        sortable: true,
        filterable: true,
        selectable: false,
        dataProperty: 'status',
        type: 'component',
        data: {
          name: InvoiceStatusComponent,
          additionData: true,
          aData: { fetch_key: 'status' },
        },
      },
      {
        title: '',
        classes: ' py-2  white-space-nowrap',
        dataProperty: 'actions',
        type: 'component',
        data: {
          name: ActionsComponent,
          additionData: true,
          aData: [
            'print',
            'sendemail',
            'sendwhatsapp',
            'sendmobilenotification',
            'void',
            'refund',
          ],
        },
      },
    ]);
  }

  onPaymentActionEvent(event: any) {
    switch (event.action) {
      case 'openpayment':
        {
          this.paymentDetails = event.columnData;
          this.paymentId = this.paymentDetails.id;
          this.GetPaymentPdf().then((res) => {
            this.showPaymentPopup.next(true);
          });
        }
        break;
      case 'openinvoice':
        {
          this.OpenInvoicePopup(event.columnData);
        }
        break;
      case 'print':
        {
          this.paymentId = event.columnData.id;
          this.paymentDetails = event.columnData;
          this.GetPaymentPdf().then((res) => {
            this.printPaymentPDF();
          });
        }
        break;
      case 'sendemail':
        {
          this.paymentDetails = event.columnData;
          this.paymentId = event.columnData.id;
          this.SendPaymentPdf('Email');
        }
        break;
      case 'sendwhatsapp':
        {
          this.paymentDetails = event.columnData;
          this.paymentId = event.columnData.id;
          this.SendPaymentPdf('WhatsApp');
        }
        break;
      case 'sendmobilenotification':
        {
          this.paymentDetails = event.columnData;
          this.paymentId = event.columnData.id;
          this.SendPaymentPdf('Push');
        }
        break;
      case 'void':
        {
          this.paymentId = event.columnData.id;
          this.paymentDetails = event.columnData;
          if (
            this.paymentDetails.status.toLocaleLowerCase() != PaymentStatus.Void
          )
            this.showVoidPaymentModal = true;
        }
        break;
      case 'collectdues':
        break;
      case 'refund':
        {
          this.paymentId = event.columnData.id;
          this.paymentService
            .getPaymentByPaymentId(this.clientId(), this.paymentId)
            .subscribe({
              next: (data: any) => {
                this.paymentDetails = event.columnData;
                this.refundData.amount =
                  this.paymentDetails.amount_in_minor_currency;
                this.refundData.clientId = this.paymentDetails.client_id;
                this.refundData.primaryId = this.paymentId;
                this.refundData.tenantId =
                  this.paymentDetails.collected_at_tenant_id;
                this.refundData.refundType = RefundType.Payment;
                this.refundData.isOnline = this.paymentDetails.is_online;
                this.refundData.paymentGateway =
                  this.paymentDetails.payment_gateway_used;
                this.refundData.paymentMethods = data.payment_method;
                this.showRefund.next(true);
                this.cd.detectChanges();
              },
              error: (error: AppErrors) => {
                this.paymentDetails = event.columnData;
                this.paymentId = event.columnData.id;
                this.refundData.amount =
                  this.paymentDetails.amount_in_minor_currency;
                this.refundData.clientId = this.paymentDetails.client_id;
                this.refundData.primaryId = this.paymentId;
                this.refundData.tenantId =
                  this.paymentDetails.collected_at_tenant_id;
                this.refundData.refundType = RefundType.Payment;
                this.refundData.isOnline = this.paymentDetails.is_online;
                this.refundData.paymentGateway =
                  this.paymentDetails.payment_gateway_used;
                this.refundData.paymentMethods = null;
                this.showRefund.next(true);
                this.cd.detectChanges();
              },
            });
        }
        break;
      case 'writeoff':
        break;
      case 'updatedata':
        {
          this.GetPayments();
        }
        break;
    }
  }

  OpenInvoicePopup(invoiceNumber: string) {
    this.paymentData$.value.forEach((p) => {
      this.invoiceDetails = p.invoices.find((inv) => {
        return inv.number_prefix == invoiceNumber;
      })!;
    });
    this.invoiceId = this.invoiceDetails!.id;
    this.number_prefix = this.invoiceDetails!.number_prefix;
    this.showInvoicePopup = true;
  }

  GetPaymentPdf() {
    const promise: Promise<boolean> = new Promise((resolve, reject) => {
      this.paymentService
        .getPaymentPDF(this.clientId(), this.paymentId)
        .subscribe({
          next: (data: any) => {
            this.pdfSrc.next(data.url);
            resolve(true);
          },
          error: (error: AppErrors) => {
            this.ShowToastErrors(error);
            resolve(false);
          },
        });
    });
    return promise;
  }

  printPaymentPDF() {
    if (this.pdfSrc.value) window.open(this.pdfSrc.value, '_blank');
  }

  downloadPaymentPDF() {
    const link = document.createElement('a');
    link.setAttribute('target', '_blank');
    link.setAttribute('href', this.pdfSrc.value);
    link.setAttribute('download', `invoice.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  SendPaymentPdf(channel: string) {
    const channels: any[] = [];
    channels.push(channel);
    this.paymentService
      .SendPaymentPDF(this.clientId(), this.paymentId, channels)
      .subscribe({
        next: (data: any) => {
          this.translate
            .get('TRANSACTION.MSG.PAYMENT_MAIL_MSG')
            .subscribe((translation) => {
              this.toastMessageService.showSuccess(translation);
            });
        },
        error: (error: AppErrors) => {
          if (error.originalError.status == 200) {
            this.toastMessageService.showSuccess(
              error.originalError.error.text,
            );
          } else if (
            error.originalError.error &&
            Array.isArray(error.originalError.error) &&
            error.originalError.error.length > 0
          ) {
            error.originalError.error.forEach((element: NotificationError) => {
              if (element.success) {
                this.translate
                  .get('TRANSACTION.MSG.PAYMENT_MAIL_MSG')
                  .subscribe((translation) => {
                    this.toastMessageService.showSuccess(translation);
                  });
              } else
                this.toastMessageService.showError(element.error.errorMessage);
            });
          } else {
            this.ShowToastErrors(error);
          }
        },
      });
  }

  deletePayment(form: UntypedFormGroup) {
    this.btnLoading.next(true);
    if (form.valid) {
      const voidRequest: voidInvoiceRequest = {} as voidInvoiceRequest;
      voidRequest.void_reason = form.value.void_reason;
      const payment = this.paymentData$
        .getValue()
        .find((o) => o.id == this.paymentId)!;
      this.paymentService
        .voidPayment(
          payment?.collected_at_tenant_id,
          this.clientId(),
          this.paymentId,
          voidRequest,
        )
        .subscribe({
          next: (data: any) => {
            this.translate
              .get('TRANSACTION.MSG.PAYMENT_VOID_MSG')
              .subscribe((translation) => {
                this.toastMessageService.showSuccess(translation);
              });
            this.voidPaymentForm.reset();
            this.paymentDetails.status = PaymentStatus.Void;
            this.paymentData$.value.forEach((pmt) => {
              if (pmt.id == this.paymentDetails.id)
                pmt.status = this.paymentDetails.status;
            });
            this.paymentData$.next(this.paymentData$.value);
            this.GetTranslatedColums();
            this.btnLoading.next(false);
            this.showVoidPaymentModal = false;
            this.updateData.emit({ action: 'updated' });
          },
          error: (error: AppErrors) => {
            this.btnLoading.next(false);
            this.ShowToastErrors(error);
          },
        });
    } else {
      Object.keys(this.voidPaymentForm.controls).forEach((field) => {
        const control = this.voidPaymentForm.get(field);
        control?.markAsTouched({ onlySelf: true });
      });
      this.btnLoading.next(false);
    }
  }

  closePaymentPopup() {
    this.showPaymentPopup.next(false);
  }

  closeInvoicePopup(event: any) {
    this.showInvoicePopup = event;
  }

  closeRefundDialogEvent(event: any) {
    this.showRefund.next(false);
    if (event.reloadData) {
      this.GetPayments();
    }
  }

  fetchNextSetData(e: any) {
    this.paymentParams.set('PageNumber', { name: 'PageNumber', value: e.pn });
    this.paymentParams.set('PageSize', { name: 'PageSize', value: e.ps });
    this.GetPayments();
  }

  handleEvent(e: any) {
    console.log(e);
  }

  closeSidebar(event: any) {
    if (event.uuid) {
      // Toast is already shown by credit-balance-sidebar component
      // Removed duplicate toast message from here
      this.GetPayments();
      this.updateData.emit({ action: 'updated' });
    }
    this.addCreditSidebar = false;
  }

  handleSelectAll(selection: any) {
    if (selection.checked) {
      this.showBulkActionBar = true;
    } else {
      this.showBulkActionBar = false;
    }
  }

  ShowToastErrors(error: AppErrors) {
    this.btnLoading.next(false);
    this.toastMessageService.showError(error);
  }

  get VoidReasonFormControl(): UntypedFormControl {
    return this.voidPaymentForm.controls.void_reason as UntypedFormControl;
  }

  get getPaymentParams(): Array<any> {
    return [...this.paymentParams.values()];
  }
}
